// src/hooks/useDocumentPiP.js

import { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { isMobile, isTablet } from 'react-device-detect';

export const useDocumentPiP = ({
  onClose,
  autoOpenOnTabChange = false,
  shouldAutoOpen = null, // Optional callback to check if PiP should open (e.g., based on current route)
} = {}) => {
  const [isPiPSupported, setIsPiPSupported] = useState(false);
  const [pipWindow, setPipWindow] = useState(null);
  const [userDismissed, setUserDismissed] = useState(false);

  const onCloseRef = useRef(onClose);
  const autoOpenRef = useRef(autoOpenOnTabChange);
  const openPiPRef = useRef(null);
  const pipWindowRef = useRef(null);
  const isPiPSupportedRef = useRef(false);
  const verifyIntervalRef = useRef(null);
  const userDismissedRef = useRef(false);
  const manualOpenTimestampRef = useRef(null); // Track when manual open happened
  const isManualOpenRef = useRef(false); // Track if current PIP window was opened manually
  const recentlyManuallyOpenedRef = useRef(false); // Track if PIP was recently manually opened (to prevent immediate close)
  const wasManuallyOpenedThisSessionRef = useRef(false); // Track if PIP was manually opened at any point this session

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    autoOpenRef.current = autoOpenOnTabChange;
  }, [autoOpenOnTabChange]);

  useEffect(() => {
    // Check for Document Picture-in-Picture API support
    // Disable PIP on mobile devices (mobile and tablet)
    const isMobileDevice = isMobile || isTablet;
    const supported = !isMobileDevice && 'documentPictureInPicture' in window;
    setIsPiPSupported(supported);
    isPiPSupportedRef.current = supported;
  }, []);

  // Initialize userDismissed state from sessionStorage on hook mount
  useEffect(() => {
    const dismissed = sessionStorage.getItem('pipDismissedByUser') === 'true';
    setUserDismissed(dismissed);
    userDismissedRef.current = dismissed;
  }, []);

  // Keep userDismissedRef in sync with userDismissed state
  useEffect(() => {
    userDismissedRef.current = userDismissed;
  }, [userDismissed]);

  // Update sessionStorage when userDismissed state changes
  useEffect(() => {
    sessionStorage.setItem('pipDismissedByUser', userDismissed.toString());
  }, [userDismissed]);

  // Keep pipWindowRef in sync with pipWindow state
  useEffect(() => {
    pipWindowRef.current = pipWindow;
  }, [pipWindow]);

  // Helper function to check if a window is valid
  const isWindowValid = useCallback(window => {
    if (!window) return false;
    try {
      if (window.closed) return false;
      // Try to access document to verify window is still valid
      const doc = window.document;
      return !!doc;
    } catch (e) {
      // Can't access document, window is likely closed
      return false;
    }
  }, []);

  // Periodic check to verify PiP window is still valid (catches cases where window is closed externally)
  useEffect(() => {
    if (!pipWindow) {
      // Clear verification interval if window is closed
      if (verifyIntervalRef.current) {
        clearInterval(verifyIntervalRef.current);
        verifyIntervalRef.current = null;
      }
      return;
    }

    const verifyWindowState = () => {
      if (!pipWindowRef.current) {
        return;
      }

      if (!isWindowValid(pipWindowRef.current)) {
        // Window is invalid or closed (e.g. user clicked X on PiP). Mark as dismissed
        // so PiP only reopens when user clicks the PiP icon.
        setUserDismissed(true);
        userDismissedRef.current = true;
        sessionStorage.setItem('pipDismissedByUser', 'true');
        wasManuallyOpenedThisSessionRef.current = false;
        setPipWindow(null);
        pipWindowRef.current = null;
        if (verifyIntervalRef.current) {
          clearInterval(verifyIntervalRef.current);
          verifyIntervalRef.current = null;
        }
        if (onCloseRef.current) {
          onCloseRef.current({ fromPagehide: false });
        }
      }
    };

    // Check every 500ms to verify window is still valid
    verifyIntervalRef.current = setInterval(verifyWindowState, 500);

    return () => {
      if (verifyIntervalRef.current) {
        clearInterval(verifyIntervalRef.current);
        verifyIntervalRef.current = null;
      }
    };
  }, [pipWindow, isWindowValid]);

  const openPiP = useCallback(
    async ({ width = 400, height = 200, manual = false } = {}) => {
      // Use refs to check current state to avoid stale closures
      if (!isPiPSupportedRef.current) {
        return;
      }

      // If this is a manual open (user clicked the button), always reset the dismissed state
      // This ensures that clicking the button re-enables PIP for subsequent auto-opens
      if (manual) {
        setUserDismissed(false);
        userDismissedRef.current = false;
        // Immediately update sessionStorage to ensure it's synced right away
        sessionStorage.setItem('pipDismissedByUser', 'false');
        // Track when manual open happened to prevent pagehide from immediately setting it back to true
        manualOpenTimestampRef.current = Date.now();
        isManualOpenRef.current = true; // Mark that this will be a manual open
        recentlyManuallyOpenedRef.current = true; // Mark as recently manually opened to prevent immediate close
        wasManuallyOpenedThisSessionRef.current = true; // Mark that PIP was manually opened this session

        // Keep the recently manually opened flag for a very long period
        // This allows PIP to stay open when user switches tabs (PIP persists across tab switches)
        // Once user manually opens PIP, it should stay open until they explicitly close it
        // or until recording stops. We use a long grace period (5 minutes) to ensure PIP
        // persists across multiple tab switches.
        setTimeout(() => {
          recentlyManuallyOpenedRef.current = false;
        }, 300000); // 5 minute grace period - allows PIP to persist across many tab switches
      } else {
        isManualOpenRef.current = false; // Not a manual open
        recentlyManuallyOpenedRef.current = false;
      }

      // Check if user has dismissed PiP this session (only block auto-opens)
      if (!manual && userDismissedRef.current) {
        return;
      }

      // Check if PiP window exists and is actually valid
      // For manual opens, we still want to reset the state even if window is already open
      if (pipWindowRef.current && isWindowValid(pipWindowRef.current)) {
        if (manual) {
          // Manual open: state is already reset above
          // Mark as recently manually opened so it doesn't get closed immediately
          recentlyManuallyOpenedRef.current = true;
          wasManuallyOpenedThisSessionRef.current = true; // Mark that PIP was manually opened this session
          setTimeout(() => {
            recentlyManuallyOpenedRef.current = false;
          }, 300000); // 5 minute grace period - same as new window opens
        } else {
          // PiP already open
        }
        return;
      }

      // Clear any invalid window reference
      if (pipWindowRef.current) {
        setPipWindow(null);
        pipWindowRef.current = null;
      }

      try {
        // Standard Document Picture-in-Picture API
        // NOTE: This API requires user activation (must be called in response to a user gesture)
        if (!('documentPictureInPicture' in window)) {
          throw new Error('Document Picture-in-Picture API not available');
        }

        // console.log('Opening PiP window...');
        const newPipWindow =
          await window.documentPictureInPicture.requestWindow({
            width,
            height,
          });

        // The 'pagehide' event is the correct way to detect user closing the window.
        newPipWindow.addEventListener('pagehide', () => {
          // When the user closes the window, we must sync state.
          // Mark as dismissed by user since they explicitly closed it
          // BUT: Don't mark as dismissed if this was just manually opened
          // This prevents race condition where pagehide fires immediately after opening
          const timeSinceManualOpen = manualOpenTimestampRef.current 
            ? Date.now() - manualOpenTimestampRef.current 
            : Infinity;
          const wasManualOpen = isManualOpenRef.current;
          
          // Only mark as dismissed if:
          // 1. It wasn't a manual open, OR
          // 2. It was a manual open but enough time has passed (> 2 seconds)
          // This ensures the window actually opened and user intentionally closed it
          if (!wasManualOpen || timeSinceManualOpen > 2000) {
            setUserDismissed(true);
            userDismissedRef.current = true;
            sessionStorage.setItem('pipDismissedByUser', 'true');
            // If user explicitly closes PIP window, reset the session flag
            wasManuallyOpenedThisSessionRef.current = false;
          } else {
            // Keep the dismissed state as false since this was a manual open
            setUserDismissed(false);
            userDismissedRef.current = false;
            sessionStorage.setItem('pipDismissedByUser', 'false');
          }
          
          // Reset the flags
          manualOpenTimestampRef.current = null;
          isManualOpenRef.current = false;
          
        setPipWindow(null);
        pipWindowRef.current = null;
        if (verifyIntervalRef.current) {
          clearInterval(verifyIntervalRef.current);
          verifyIntervalRef.current = null;
        }
        if (onCloseRef.current) {
          onCloseRef.current({ fromPagehide: true });
        }
      });

        setPipWindow(newPipWindow);
        pipWindowRef.current = newPipWindow;
      } catch (pipError) {
        // Check if it's a user activation error
        if (
          pipError.name === 'NotAllowedError' ||
          pipError.message?.includes('user activation') ||
          pipError.message?.includes('user gesture')
        ) {
          const errorMsg =
            'Document Picture-in-Picture requires a user interaction (click). Please click the PiP button to open the window.';
          throw new Error(errorMsg);
        }
        throw pipError;
      }
    },
    [isWindowValid]
  );

  // Store openPiP in ref so it can be accessed in the visibility change handler
  useEffect(() => {
    openPiPRef.current = openPiP;
  }, [openPiP]);

  const closePiP = useCallback((markAsDismissed = false) => {
    if (pipWindowRef.current) {
      // If this is a programmatic close (not user dismissing), don't mark as dismissed
      // unless explicitly requested
      // However, if PIP was recently manually opened, don't mark as dismissed
      if (markAsDismissed && !recentlyManuallyOpenedRef.current) {
        setUserDismissed(true);
        userDismissedRef.current = true;
        sessionStorage.setItem('pipDismissedByUser', 'true');
        // If user explicitly closes PIP, reset the session flag
        wasManuallyOpenedThisSessionRef.current = false;
      }
      
      // We directly close the window. The 'pagehide' event will NOT fire for
      // programmatic closing, so we manually update state.
      pipWindowRef.current.close();
      setPipWindow(null);
      pipWindowRef.current = null;
      if (verifyIntervalRef.current) {
        clearInterval(verifyIntervalRef.current);
        verifyIntervalRef.current = null;
      }
      if (onCloseRef.current) {
        onCloseRef.current({ fromPagehide: false });
      }
    }
  }, []);

  // Reset PIP preference - allows PIP to open again after user has dismissed it
  const resetPiPPreference = useCallback(() => {
    setUserDismissed(false);
    userDismissedRef.current = false;
  }, []);

  // Store shouldAutoOpen callback in ref
  const shouldAutoOpenRef = useRef(shouldAutoOpen);
  useEffect(() => {
    shouldAutoOpenRef.current = shouldAutoOpen;
  }, [shouldAutoOpen]);

  // Proactively open PiP when autoOpenOnTabChange becomes true (recording starts)
  // This ensures PiP is already open when user switches tabs, avoiding user activation issues
  // Use useLayoutEffect to run synchronously and preserve user activation context
  useLayoutEffect(() => {
    if (!isPiPSupportedRef.current || !autoOpenOnTabChange) {
      return;
    }

    // Check if we should auto-open based on the callback (e.g., route check)
    if (shouldAutoOpenRef.current && !shouldAutoOpenRef.current()) {
      return; // Don't open if callback returns false
    }

    // Check if PiP window is already open and valid
    const isWindowCurrentlyValid =
      pipWindowRef.current && isWindowValid(pipWindowRef.current);

    if (!isWindowCurrentlyValid && openPiPRef.current) {
      // Clear any invalid window reference
      if (pipWindowRef.current) {
        setPipWindow(null);
        pipWindowRef.current = null;
      }

      // Use queueMicrotask to ensure this runs in the same event loop
      // This helps preserve user activation context from the recording start action
      queueMicrotask(() => {
        if (openPiPRef.current && autoOpenRef.current) {
          // Check again before opening (route might have changed)
          if (shouldAutoOpenRef.current && !shouldAutoOpenRef.current()) {
            return; // Don't open if callback returns false
          }
          openPiPRef.current({ width: 400, height: 200 }).catch(error => {
            // Handle user activation errors - this might happen if called too late
            if (
              error.name === 'NotAllowedError' ||
              error.message?.includes('user activation') ||
              error.message?.includes('user gesture')
            ) {
              // PiP auto-open requires user activation; will attempt on window blur as fallback
            }
          });
        }
      });
    }
  }, [autoOpenOnTabChange, isWindowValid]);

  // Handle window blur event - try to open PiP when window loses focus
  // This might still have user activation context from the user's action
  useEffect(() => {
    if (!isPiPSupportedRef.current) {
      return;
    }

    const handleWindowBlur = () => {
      // Only try if auto-open is enabled and PiP is not already open
      // NOTE: We don't check shouldAutoOpen here because we want PiP to open
      // when user switches tabs regardless of the current route
      if (autoOpenRef.current && openPiPRef.current) {
        const isWindowCurrentlyValid =
          pipWindowRef.current && isWindowValid(pipWindowRef.current);

        if (!isWindowCurrentlyValid) {
          // Clear any invalid window reference
          if (pipWindowRef.current) {
            setPipWindow(null);
            pipWindowRef.current = null;
          }

          // Try to open PiP when window loses focus (user might be switching tabs)
          // This might still have user activation context
          openPiPRef.current({ width: 400, height: 200 }).catch(error => {
            // Silently handle user activation errors
            if (
              error.name === 'NotAllowedError' ||
              error.message?.includes('user activation') ||
              error.message?.includes('user gesture')
            ) {
              // PiP auto-open on blur requires user activation
            }
          });
        }
      }
    };

    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [isWindowValid]);

  // Handle tab visibility changes - try to open PiP when tab becomes hidden
  useEffect(() => {
    if (!isPiPSupportedRef.current) {
      return;
    }

    const handleVisibilityChange = () => {
      // When tab becomes visible, verify PiP window is still valid
      if (!document.hidden && pipWindowRef.current) {
        if (!isWindowValid(pipWindowRef.current)) {
          // Window is invalid or closed, clear state
          setPipWindow(null);
          pipWindowRef.current = null;
          if (verifyIntervalRef.current) {
            clearInterval(verifyIntervalRef.current);
            verifyIntervalRef.current = null;
          }
        }
        return; // Don't try to open PiP when tab becomes visible
      }

      // When tab becomes hidden, try to open PiP if it's not already open
      // Check shouldAutoOpen callback and userDismissed state before opening
      if (document.hidden && autoOpenRef.current && openPiPRef.current) {
        // First, check if PIP window is already open and valid - if so, it should stay open
        const isWindowCurrentlyValid =
          pipWindowRef.current && isWindowValid(pipWindowRef.current);
        
        if (isWindowCurrentlyValid) {
          return; // Window is already open, no need to open again
        }

        // Check if user has dismissed PIP - don't auto-open if dismissed
        if (userDismissedRef.current) {
          return;
        }

        // Check shouldAutoOpen callback if provided (pass tabHidden so callback can allow open when switching tabs from home)
        if (shouldAutoOpenRef.current) {
          const shouldOpen = shouldAutoOpenRef.current({ tabHidden: true });
          if (!shouldOpen) {
            return;
          }
        }

        // Window is not valid, try to open it
        if (!isWindowCurrentlyValid) {
          // Clear any invalid window reference
          if (pipWindowRef.current) {
            setPipWindow(null);
            pipWindowRef.current = null;
          }

          // Small delay to ensure the tab change is complete
          setTimeout(() => {
            // Double-check conditions after delay
            if (document.hidden && autoOpenRef.current && openPiPRef.current) {
              // Check again if user dismissed
              if (userDismissedRef.current) {
                return;
              }

              // Check shouldAutoOpen again (tab is hidden when opening on tab switch)
              if (shouldAutoOpenRef.current) {
                const shouldOpen = shouldAutoOpenRef.current({ tabHidden: true });
                if (!shouldOpen) {
                  return;
                }
              }

              // Verify again that window is not valid
              const stillInvalid =
                !pipWindowRef.current || !isWindowValid(pipWindowRef.current);

              if (stillInvalid) {
                // Clear any invalid reference
                if (pipWindowRef.current) {
                  setPipWindow(null);
                  pipWindowRef.current = null;
                }

                // Try to open PiP when tab becomes hidden
                // Note: Document PiP API requires user activation, so this may fail silently
                // when triggered by tab switching (not a user gesture)
                // However, if the user recently clicked the PIP button, we should try anyway
                // as the state has been reset and they've shown intent to use PIP
                openPiPRef.current({ width: 400, height: 200 }).catch(() => {
                  // Handle user activation errors for Document PiP API (expected when triggered by tab switching)
                });
              }
            }
          }, 100);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isWindowValid]); // Include isWindowValid in deps

  // !! IMPORTANT CLEANUP EFFECT !!
  // This effect runs when the component using the hook unmounts.
  useEffect(() => {
    return () => {
      // If the component is unmounting and a PiP window still exists, close it.
      if (pipWindowRef.current) {
        try {
          pipWindowRef.current.close();
        } catch (e) {
          // Ignore errors when closing
        }
      }
      if (verifyIntervalRef.current) {
        clearInterval(verifyIntervalRef.current);
        verifyIntervalRef.current = null;
      }
    };
  }, []); // Empty deps - using ref

  // Expose a way to check if PIP was recently manually opened
  const wasRecentlyManuallyOpened = useCallback(() => {
    // Return true if recently manually opened OR if it was manually opened this session
    // This ensures PIP stays open even after grace period if it was manually opened
    return recentlyManuallyOpenedRef.current || wasManuallyOpenedThisSessionRef.current;
  }, []);

  return {
    pipWindow,
    isPiPSupported,
    isPiPOpen: !!pipWindow,
    openPiP,
    closePiP,
    userDismissed,
    resetPiPPreference,
    wasRecentlyManuallyOpened,
  };
};
